import React, { useState } from 'react';
import { SettingsIcon, ShieldIcon, BellIcon, DatabaseIcon, AlertTriangleIcon, InfoIcon, SaveIcon, TrashIcon, RefreshCwIcon, CheckIcon, BanknoteIcon, EyeIcon, EyeOffIcon, KeyIcon, LayoutGridIcon } from 'lucide-react';
export function Settings() {
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  // General Settings State
  const [general, setGeneral] = useState({
    municipalityName: 'City of Johannesburg Metropolitan',
    timezone: 'Africa/Johannesburg',
    currency: 'ZAR',
    language: 'English',
    meterAccountGroup: 'A901-JHB-MUN-7X42-KE9F'
  });
  const [showAccountGroup, setShowAccountGroup] = useState(false);
  // Modules State
  const [modules, setModules] = useState({
    vending: true,
    smartMeters: true,
    gridBalance: false,
    revenue: true
  });
  // Tariff Configuration State
  const [tariff, setTariff] = useState({
    currentRate: '2.50',
    vatRate: '15',
    freeBasic: '50',
    lifelineRate: '0.85'
  });
  // Notification Preferences State
  const [notifications, setNotifications] = useState({
    emailAlerts: true,
    smsNotifications: true,
    tamperDetection: true,
    lowCredit: false,
    dailySummary: true,
    offlineMeter: true
  });
  // Alert Thresholds State
  const [thresholds, setThresholds] = useState({
    highConsumption: '50',
    offlineDuration: '24',
    lowCredit: '20'
  });
  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 3000);
    }, 1000);
  };
  const toggleNotification = (key: keyof typeof notifications) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const toggleModule = (key: keyof typeof modules) => {
    setModules(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  return <div className="p-8 max-w-7xl mx-auto animate-in fade-in duration-500" data-id="element-2090">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4" data-id="element-2091">
        <div data-id="element-2092">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2093">
            System Settings
          </h1>
          <p className="text-gray-500" data-id="element-2094">
            Configure system preferences, tariffs, and notifications.
          </p>
        </div>
        <button onClick={handleSave} disabled={isSaving} className={`flex items-center gap-2 px-6 py-3 font-medium rounded-none transition-colors shadow-sm ${isSaved ? 'bg-mavis-green text-white' : 'bg-mavis-teal text-white hover:bg-mavis-teal/90'} disabled:opacity-70`} data-id="element-2095">

          {isSaving ? <RefreshCwIcon className="w-5 h-5 animate-spin" data-id="element-2096" /> : isSaved ? <CheckIcon className="w-5 h-5" data-id="element-2097" /> : <SaveIcon className="w-5 h-5" data-id="element-2098" />}
          {isSaving ? 'Saving...' : isSaved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      <div className="space-y-8" data-id="element-2099">
        {/* General Settings */}
        <div className="bg-white rounded-none shadow-sm border border-gray-200" data-id="element-2100">
          <div className="p-6 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50" data-id="element-2101">
            <SettingsIcon className="w-5 h-5 text-mavis-teal" data-id="element-2102" />
            <h2 className="text-lg font-bold text-gray-900" data-id="element-2103">
              General Configuration
            </h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2104">
            <div data-id="element-2105">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2106">
                Municipality Name
              </label>
              <input type="text" value={general.municipalityName} onChange={e => setGeneral({
              ...general,
              municipalityName: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm" data-id="element-2107" />

            </div>
            <div data-id="element-2108">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2109">
                System Timezone
              </label>
              <select value={general.timezone} onChange={e => setGeneral({
              ...general,
              timezone: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm bg-white" data-id="element-2110">

                <option value="Africa/Johannesburg" data-id="element-2111">Africa/Johannesburg</option>
                <option value="Africa/Cairo" data-id="element-2112">Africa/Cairo</option>
                <option value="UTC" data-id="element-2113">UTC</option>
              </select>
            </div>
            <div data-id="element-2114">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2115">
                Currency
              </label>
              <select value={general.currency} onChange={e => setGeneral({
              ...general,
              currency: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm bg-white" data-id="element-2116">

                <option value="ZAR" data-id="element-2117">ZAR - South African Rand</option>
                <option value="USD" data-id="element-2118">USD - US Dollar</option>
                <option value="EUR" data-id="element-2119">EUR - Euro</option>
              </select>
            </div>
            <div data-id="element-2120">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2121">
                Language
              </label>
              <select value={general.language} onChange={e => setGeneral({
              ...general,
              language: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm bg-white" data-id="element-2122">

                <option value="English" data-id="element-2123">English</option>
                <option value="Afrikaans" data-id="element-2124">Afrikaans</option>
                <option value="Zulu" data-id="element-2125">Zulu</option>
                <option value="Xhosa" data-id="element-2126">Xhosa</option>
              </select>
            </div>
            <div className="md:col-span-2" data-id="element-2127">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2" data-id="element-2128">
                <KeyIcon className="w-4 h-4 text-mavis-teal" data-id="element-2129" />
                Meter Account Group
              </label>
              <div className="relative" data-id="element-2130">
                <input type={showAccountGroup ? 'text' : 'password'} value={general.meterAccountGroup} onChange={e => setGeneral({
                ...general,
                meterAccountGroup: e.target.value
              })} className="w-full px-4 pr-12 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm font-mono tracking-wider" data-id="element-2131" />

                <button type="button" onClick={() => setShowAccountGroup(!showAccountGroup)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2132">

                  {showAccountGroup ? <EyeOffIcon className="w-5 h-5" data-id="element-2133" /> : <EyeIcon className="w-5 h-5" data-id="element-2134" />}
                </button>
              </div>
              <div className="mt-3 bg-mavis-teal/5 border border-mavis-teal/20 p-3 flex items-start gap-3" data-id="element-2135">
                <ShieldIcon className="w-5 h-5 text-mavis-teal shrink-0 mt-0.5" data-id="element-2136" />
                <p className="text-xs text-gray-600" data-id="element-2137">
                  <strong className="text-gray-900" data-id="element-2138">
                    Sensitive credential.
                  </strong>{' '}
                  The Meter Account Group is a secret key used to generate and
                  validate prepaid electricity tokens associated with this
                  municipality. Do not share this value. Changes will affect all
                  future token generation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Modules */}
        <div className="bg-white rounded-none shadow-sm border border-gray-200" data-id="element-2139">
          <div className="p-6 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50" data-id="element-2140">
            <LayoutGridIcon className="w-5 h-5 text-mavis-teal" data-id="element-2141" />
            <h2 className="text-lg font-bold text-gray-900" data-id="element-2142">Modules</h2>
          </div>
          <div className="p-6" data-id="element-2143">
            <p className="text-sm text-gray-500 mb-5" data-id="element-2144">
              Enable or disable system modules. Disabled modules will be hidden
              from the sidebar and inaccessible to all users.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2145">
              {[{
              id: 'vending',
              label: 'Vending',
              desc: 'Prepaid electricity token vending and purchase management.'
            }, {
              id: 'smartMeters',
              label: 'Smart Meters',
              desc: 'Real-time smart meter monitoring, mapping, and diagnostics.'
            }, {
              id: 'gridBalance',
              label: 'Grid Balance',
              desc: 'Grid load balancing, substation monitoring, and loss analytics.'
            }, {
              id: 'revenue',
              label: 'Revenue',
              desc: 'Revenue tracking, billing integrations, and financial reporting.'
            }].map(item => {
              const key = item.id as keyof typeof modules;
              const isChecked = modules[key];
              return <label key={key} className={`flex items-start gap-4 p-4 border cursor-pointer transition-colors ${isChecked ? 'border-mavis-teal/30 bg-mavis-teal/5' : 'border-gray-200 bg-white hover:bg-gray-50'}`} data-id="element-2146">

                    <div className="pt-0.5" data-id="element-2147">
                      <div className={`w-5 h-5 rounded-none border-2 flex items-center justify-center transition-colors ${isChecked ? 'bg-mavis-teal border-mavis-teal' : 'border-gray-300 bg-white'}`} data-id="element-2148">

                        {isChecked && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-2149" />}
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-2150">
                      <p className="text-sm font-bold text-gray-900" data-id="element-2151">
                        {item.label}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-2152">{item.desc}</p>
                    </div>
                    <input type="checkbox" checked={isChecked} onChange={() => toggleModule(key)} className="sr-only" data-id="element-2153" />

                  </label>;
            })}
            </div>
          </div>
        </div>

        {/* Tariff Configuration */}
        <div className="bg-white rounded-none shadow-sm border border-gray-200" data-id="element-2154">
          <div className="p-6 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50" data-id="element-2155">
            <BanknoteIcon className="w-5 h-5 text-mavis-teal" data-id="element-2156" />
            <h2 className="text-lg font-bold text-gray-900" data-id="element-2157">
              Tariff & Pricing
            </h2>
          </div>
          <div className="p-6" data-id="element-2158">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-2159">
              <div data-id="element-2160">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2161">
                  Current Tariff Rate
                </label>
                <div className="relative" data-id="element-2162">
                  <input type="number" step="0.01" value={tariff.currentRate} onChange={e => setTariff({
                  ...tariff,
                  currentRate: e.target.value
                })} className="w-full pl-4 pr-16 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm font-mono" data-id="element-2163" />

                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none" data-id="element-2164">
                    <span className="text-gray-500 text-sm" data-id="element-2165">R/kWh</span>
                  </div>
                </div>
              </div>
              <div data-id="element-2166">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2167">
                  VAT Rate
                </label>
                <div className="relative" data-id="element-2168">
                  <input type="number" value={tariff.vatRate} onChange={e => setTariff({
                  ...tariff,
                  vatRate: e.target.value
                })} className="w-full pl-4 pr-12 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm font-mono" data-id="element-2169" />

                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none" data-id="element-2170">
                    <span className="text-gray-500 text-sm" data-id="element-2171">%</span>
                  </div>
                </div>
              </div>
              <div data-id="element-2172">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2173">
                  Free Basic Electricity
                </label>
                <div className="relative" data-id="element-2174">
                  <input type="number" value={tariff.freeBasic} onChange={e => setTariff({
                  ...tariff,
                  freeBasic: e.target.value
                })} className="w-full pl-4 pr-24 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm font-mono" data-id="element-2175" />

                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none" data-id="element-2176">
                    <span className="text-gray-500 text-sm" data-id="element-2177">kWh/month</span>
                  </div>
                </div>
              </div>
              <div data-id="element-2178">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2179">
                  Lifeline Tariff Rate
                </label>
                <div className="relative" data-id="element-2180">
                  <input type="number" step="0.01" value={tariff.lifelineRate} onChange={e => setTariff({
                  ...tariff,
                  lifelineRate: e.target.value
                })} className="w-full pl-4 pr-16 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm font-mono" data-id="element-2181" />

                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none" data-id="element-2182">
                    <span className="text-gray-500 text-sm" data-id="element-2183">R/kWh</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-100 p-4 flex items-start gap-3" data-id="element-2184">
              <InfoIcon className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" data-id="element-2185" />
              <p className="text-sm text-blue-800" data-id="element-2186">
                <strong data-id="element-2187">Note:</strong> Changes to tariff rates and VAT will take
                effect at the start of the next billing cycle. Existing tokens
                will not be affected.
              </p>
            </div>
          </div>
        </div>

        {/* Notification Preferences */}
        <div className="bg-white rounded-none shadow-sm border border-gray-200" data-id="element-2188">
          <div className="p-6 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50" data-id="element-2189">
            <BellIcon className="w-5 h-5 text-mavis-teal" data-id="element-2190" />
            <h2 className="text-lg font-bold text-gray-900" data-id="element-2191">
              Alerts & Notifications
            </h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2192">
            {[{
            id: 'emailAlerts',
            label: 'Email Alerts',
            desc: 'Receive critical system alerts via email.'
          }, {
            id: 'smsNotifications',
            label: 'SMS Notifications',
            desc: 'Send SMS alerts for high-priority events.'
          }, {
            id: 'tamperDetection',
            label: 'Tamper Detection Alerts',
            desc: 'Immediate notification when meter tampering is detected.'
          }, {
            id: 'lowCredit',
            label: 'Low Credit Warnings',
            desc: 'Alert customers when their balance drops below threshold.'
          }, {
            id: 'dailySummary',
            label: 'Daily Summary Reports',
            desc: 'Receive a daily digest of system performance and revenue.'
          }, {
            id: 'offlineMeter',
            label: 'Offline Meter Alerts',
            desc: 'Notify when a meter goes offline for more than 24 hours.'
          }].map(item => {
            const key = item.id as keyof typeof notifications;
            const isOn = notifications[key];
            return <div key={key} className="flex items-start justify-between gap-4" data-id="element-2193">

                  <div data-id="element-2194">
                    <p className="text-sm font-bold text-gray-900" data-id="element-2195">
                      {item.label}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-2196">{item.desc}</p>
                  </div>
                  <button type="button" onClick={() => toggleNotification(key)} className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${isOn ? 'bg-mavis-teal' : 'bg-gray-200'}`} role="switch" aria-checked={isOn} data-id="element-2197">

                    <span className="sr-only" data-id="element-2198">Use setting</span>
                    <span aria-hidden="true" className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isOn ? 'translate-x-2.5' : '-translate-x-2.5'}`} data-id="element-2199" />

                  </button>
                </div>;
          })}
          </div>
        </div>

        {/* Alert Thresholds */}
        <div className="bg-white rounded-none shadow-sm border border-gray-200" data-id="element-2200">
          <div className="p-6 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50" data-id="element-2201">
            <AlertTriangleIcon className="w-5 h-5 text-mavis-teal" data-id="element-2202" />
            <h2 className="text-lg font-bold text-gray-900" data-id="element-2203">
              Alert Thresholds
            </h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2204">
            <div data-id="element-2205">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2206">
                High Consumption Alert
              </label>
              <div className="relative" data-id="element-2207">
                <input type="number" value={thresholds.highConsumption} onChange={e => setThresholds({
                ...thresholds,
                highConsumption: e.target.value
              })} className="w-full pl-4 pr-20 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm font-mono" data-id="element-2208" />

                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none" data-id="element-2209">
                  <span className="text-gray-500 text-sm" data-id="element-2210">kWh/day</span>
                </div>
              </div>
            </div>
            <div data-id="element-2211">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2212">
                Offline Duration Alert
              </label>
              <div className="relative" data-id="element-2213">
                <input type="number" value={thresholds.offlineDuration} onChange={e => setThresholds({
                ...thresholds,
                offlineDuration: e.target.value
              })} className="w-full pl-4 pr-16 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm font-mono" data-id="element-2214" />

                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none" data-id="element-2215">
                  <span className="text-gray-500 text-sm" data-id="element-2216">hours</span>
                </div>
              </div>
            </div>
            <div data-id="element-2217">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2218">
                Low Credit Threshold
              </label>
              <div className="relative" data-id="element-2219">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-2220">
                  <span className="text-gray-500 text-sm" data-id="element-2221">R</span>
                </div>
                <input type="number" value={thresholds.lowCredit} onChange={e => setThresholds({
                ...thresholds,
                lowCredit: e.target.value
              })} className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm font-mono" data-id="element-2222" />

              </div>
            </div>
          </div>
        </div>

        {/* System Information */}
        <div className="bg-white rounded-none shadow-sm border border-gray-200" data-id="element-2223">
          <div className="p-6 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50" data-id="element-2224">
            <DatabaseIcon className="w-5 h-5 text-mavis-teal" data-id="element-2225" />
            <h2 className="text-lg font-bold text-gray-900" data-id="element-2226">
              System Information
            </h2>
          </div>
          <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-y-8 gap-x-6" data-id="element-2227">
            <div data-id="element-2228">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-2229">
                System Version
              </p>
              <p className="font-mono font-bold text-gray-900" data-id="element-2230">MAVIS v3.2.1</p>
            </div>
            <div data-id="element-2231">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-2232">
                Last Backup
              </p>
              <p className="font-mono font-bold text-gray-900" data-id="element-2233">
                2026-03-07 02:00 AM
              </p>
            </div>
            <div data-id="element-2234">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-2235">
                Database Status
              </p>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-none text-xs font-medium bg-green-100 text-green-800" data-id="element-2236">
                Healthy
              </span>
            </div>
            <div data-id="element-2237">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-2238">
                API Status
              </p>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-none text-xs font-medium bg-green-100 text-green-800" data-id="element-2239">
                Operational
              </span>
            </div>
            <div data-id="element-2240">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-2241">
                Total Meters Registered
              </p>
              <p className="font-mono font-bold text-gray-900" data-id="element-2242">24,592</p>
            </div>
            <div data-id="element-2243">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-2244">
                Active Vendors
              </p>
              <p className="font-mono font-bold text-gray-900" data-id="element-2245">142</p>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white rounded-none shadow-sm border border-red-200" data-id="element-2246">
          <div className="p-6 border-b border-red-100 flex items-center gap-3 bg-red-50/50" data-id="element-2247">
            <ShieldIcon className="w-5 h-5 text-red-600" data-id="element-2248" />
            <h2 className="text-lg font-bold text-red-600" data-id="element-2249">Danger Zone</h2>
          </div>
          <div className="p-6" data-id="element-2250">
            <div className="flex flex-col sm:flex-row gap-4 mb-4" data-id="element-2251">
              <button className="flex items-center justify-center gap-2 px-6 py-2 border border-red-300 text-red-600 font-medium rounded-none hover:bg-red-50 transition-colors" data-id="element-2252">
                <RefreshCwIcon className="w-4 h-4" data-id="element-2253" />
                Reset All Settings
              </button>
              <button className="flex items-center justify-center gap-2 px-6 py-2 border border-red-300 text-red-600 font-medium rounded-none hover:bg-red-50 transition-colors" data-id="element-2254">
                <TrashIcon className="w-4 h-4" data-id="element-2255" />
                Clear System Cache
              </button>
            </div>
            <p className="text-sm text-red-500" data-id="element-2256">
              <strong data-id="element-2257">Warning:</strong> These actions are irreversible and may
              affect system performance or reset custom configurations to their
              defaults.
            </p>
          </div>
        </div>
      </div>
    </div>;
}